import { Controller } from "@hotwired/stimulus"

// "Research this aircraft" on the aircraft form (full page and the weighing
// shell's modal). Sends the identifiers the user has typed (type/variant,
// MSN, registration) to AircraftsController#research, which asks Claude to
// research the exact airframe in public sources, and applies the structured
// result as suggestions: empty fields are filled and marked, the rest lands
// in a review panel. Everything stays manually overridable — editing a
// filled field clears its AI mark, and nothing is saved until the form is
// submitted.
export default class extends Controller {
  static targets = [
    "model", "variant", "variantSelect", "registration", "serial", "operator", "button", "status", "panel",
    "engines", "cabinConfiguration", "seatLayout", "modifications", "deliveredOn", "airframeStatus", "researchLog",
    "details", "submit", "continue"
  ]
  // staged: a NEW aircraft shows only the three identifying fields and the
  // research button; the configuration, facts and operator sections (and
  // the save button) appear once research has run or the person chose to
  // fill in manually. Editing, and no-JS, show everything at once.
  // canCreateOperator / organizationsUrl: a researched operator missing from
  // the list can be added with one click by someone allowed to create
  // organisations (OrganizationPolicy#create?); everyone else is told to ask.
  static values = { url: String, staged: Boolean, canCreateOperator: Boolean, organizationsUrl: String }

  connect() {
    // Without the research section (feature not configured server-side) the
    // controller stays dormant; field actions still fire but do nothing.
    this.revealed = !this.stagedValue
    this.form = this.element.tagName === "FORM" ? this.element : this.element.querySelector("form")
    this.form?.addEventListener("submit", this.guardSubmit)
    // Constraint validation runs BEFORE submit fires: a required select
    // inside the hidden block would silently abort the submit. The first
    // invalid event reveals the block so the browser's message has a field
    // to point at.
    this.form?.addEventListener("invalid", this.revealOnInvalid, true)
    this.showDetails(this.revealed)
    this.refresh()
  }

  disconnect() {
    this.abortController?.abort()
    this.form?.removeEventListener("submit", this.guardSubmit)
    this.form?.removeEventListener("invalid", this.revealOnInvalid, true)
  }

  // ---- staging ----

  showDetails(show) {
    this.detailsTargets.forEach((el) => el.hidden = !show)
    this.submitTargets.forEach((el) => el.hidden = !show)
    this.continueTargets.forEach((el) => el.hidden = show)
  }

  reveal() {
    if (this.revealed) return
    this.revealed = true
    this.showDetails(true)
  }

  continueManually() {
    this.reveal()
    this.detailsTargets[0]?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  // Enter in one of the three identifying fields runs the research (when
  // ready) instead of submitting a form whose required fields are hidden.
  runOnEnter(event) {
    if (this.revealed) return
    event.preventDefault()
    if (this.ready()) this.run()
  }

  guardSubmit = (event) => {
    if (this.revealed) return
    event.preventDefault()
    this.continueManually()
  }

  revealOnInvalid = () => {
    if (!this.revealed) this.continueManually()
  }

  inputChanged(event) {
    if (this.filling) return
    event?.target?.classList?.remove("ai-filled")
    // A manual edit returns the status line to its idle hint.
    if (this.hasStatusTarget && !this.running) this.statusTarget.dataset.tone = "idle"
    this.refresh()
  }

  // Button state and the idle hint. The hint is written only while the
  // status line is idle: a result summary or an error stays visible until
  // the user edits a field (inputChanged resets the tone).
  refresh() {
    if (!this.hasButtonTarget) return
    const ready = this.ready()
    this.buttonTarget.disabled = !ready || this.running === true
    if (this.running) return
    const tone = this.hasStatusTarget ? (this.statusTarget.dataset.tone || "idle") : "idle"
    if (tone !== "idle") return
    this.setStatus(ready
      ? "Researches this exact airframe in public sources."
      : "Needs the aircraft type, MSN and registration.")
  }

  // All three identifiers: the type disambiguates an Airbus MSN (each
  // programme numbers from 1), the registration cross-checks the airframe.
  ready() {
    return Boolean(this.value(this.modelTargets) && this.value(this.serialTargets) && this.value(this.registrationTargets))
  }

  async run() {
    if (!this.hasButtonTarget || this.running) return
    this.running = true
    this.buttonTarget.disabled = true
    this.setStatus("Researching online — this can take a few minutes…", "busy")
    this.panelTarget.classList.add("d-none")
    this.abortController = new AbortController()

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        body: JSON.stringify({
          model: this.value(this.modelTargets),
          variant: this.value(this.variantTargets),
          registration: this.value(this.registrationTargets),
          serial_number: this.value(this.serialTargets)
        }),
        signal: this.abortController.signal
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) {
        this.setStatus(payload.error || "The research request failed. Try again, or enter the details manually.", "error")
        this.reveal()
        return
      }
      this.apply(payload.data || {})
    } catch (error) {
      if (error.name === "AbortError") return
      this.setStatus("The research request failed. Try again, or enter the details manually.", "error")
      this.reveal()
    } finally {
      this.running = false
      // Only the button is re-evaluated here — refresh() leaves a "done" or
      // "error" status line alone, so the outcome the user needs stays put.
      if (this.hasButtonTarget) this.buttonTarget.disabled = !this.ready()
    }
  }

  // ---- applying the result ----

  apply(data) {
    // Whatever the outcome, the rest of the form is needed now: either to
    // check what was filled or to fill it in by hand. Every run, found or
    // not, goes on the aircraft's research log.
    this.reveal()
    this.appendResearchLog(data)
    if (data.found === false) {
      this.setStatus("Could not identify this airframe with confidence — enter the details manually.", "error")
      this.renderPanel(data, { filled: [] })
      return
    }

    // Registration and MSN are always typed before research can run, so
    // they are never filled here; a differing registration found by the
    // research is shown in the panel and recorded in the log entry.
    const filled = []
    if (this.fillOperator(data.operator)) filled.push("operator")
    // The derived engine-generation code (CEO / NEO, from the designation)
    // matches the reviewed configs' labels; the prose variant is the fallback.
    if (this.fillVariant(data.variant_code) || this.fillVariant(data.variant)) filled.push("variant")
    if (this.fill(this.enginesTargets[0], data.engines)) filled.push("engines")
    if (this.fillSelect(this.cabinConfigurationTargets[0], data.cabin_class)) filled.push("cabin configuration")
    if (this.fill(this.seatLayoutTargets[0], data.cabin_configuration)) filled.push("seat layout")
    if (this.fill(this.modificationsTargets[0], this.list(data.modifications, "; "))) filled.push("modifications")
    if (this.fill(this.deliveredOnTargets[0], this.isoDate(data.delivered_on))) filled.push("delivery date")
    if (this.fill(this.airframeStatusTargets[0], data.airframe_status)) filled.push("airframe status")

    const summary = filled.length
      ? `Research complete — filled ${filled.join(", ")}. Review before saving.`
      : "Research complete — review the findings below."
    this.setStatus(summary, "done")
    this.renderPanel(data, { filled })
  }

  // Fills an empty input with a suggestion and marks it AI-filled; a field
  // the user already typed in is never touched.
  fill(element, value) {
    if (!element || !value || element.value.trim() !== "") return false
    this.filling = true
    element.value = value
    element.classList.add("ai-filled")
    // Let dependent controllers react (e.g. MSN drives weight-variant
    // applicability in aircraft-config-select).
    element.dispatchEvent(new Event("input", { bubbles: true }))
    element.dispatchEvent(new Event("change", { bubbles: true }))
    this.filling = false
    return true
  }

  // A select is filled only when the suggestion is one of its actual options
  // (the cabin class comes back from the research constrained to that list).
  fillSelect(select, value) {
    if (!select || !value || select.value) return false
    const option = Array.from(select.options).find((o) => o.value === value)
    if (!option) return false
    this.filling = true
    select.value = option.value
    select.classList.add("ai-filled")
    select.dispatchEvent(new Event("change", { bubbles: true }))
    this.filling = false
    return true
  }

  // The visible Variant dropdown (options come from the reviewed configs,
  // e.g. "CEO" / "NEO") is picked when the researched variant names it:
  // "A321neo" matches "NEO". Dispatching change lets aircraft-config-select
  // copy it into the hidden field and refresh the profile card. The WBM
  // profile and weight variant are never chosen by research.
  fillVariant(name) {
    if (!this.hasVariantSelectTarget || !name) return false
    const select = this.variantSelectTarget
    if (select.value) return false
    const wanted = this.normalize(name).replace(/ /g, "")
    const option = Array.from(select.options).filter((o) => o.value).find((o) => {
      const candidate = this.normalize(o.textContent).replace(/ /g, "")
      return candidate.length >= 3 && (candidate === wanted || wanted.endsWith(candidate) || candidate.endsWith(wanted))
    })
    if (!option) return false
    this.filling = true
    select.value = option.value
    select.classList.add("ai-filled")
    select.dispatchEvent(new Event("change", { bubbles: true }))
    this.filling = false
    return true
  }

  // The server composes one dated plain-text block per run (log_entry);
  // it goes on top of the aircraft's research log, older runs kept below.
  // Saved with the form like any other field, so nothing is lost or
  // written until the person saves.
  appendResearchLog(data) {
    if (!this.hasResearchLogTarget || !data.log_entry) return
    const log = this.researchLogTarget
    const existing = log.value.trim()
    this.filling = true
    log.value = existing ? `${data.log_entry}\n\n${existing}` : data.log_entry
    log.classList.add("ai-filled")
    log.dispatchEvent(new Event("input", { bubbles: true }))
    this.filling = false
  }

  isoDate(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(value || "") ? value : ""
  }

  // The operator decides which organisation can see the airframe and every
  // weighing on it, so a suggestion is applied only when it points at
  // exactly one option: an exact name, or a single option starting with the
  // researched name ("Wizz Air" against "Wizz Air Hungary Ltd." AND "Wizz Air
  // Malta Ltd." is ambiguous and is left for the human).
  fillOperator(name) {
    if (!this.hasOperatorTarget || !name) return false
    const select = this.operatorTarget
    if (select.value) return false
    const { match } = this.matchOption(select, name)
    if (!match) return false
    this.filling = true
    select.value = match.value
    select.classList.add("ai-filled")
    select.dispatchEvent(new Event("change", { bubbles: true }))
    this.filling = false
    return true
  }

  matchOption(select, name) {
    const wanted = this.normalize(name)
    const options = Array.from(select.options).filter((o) => o.value)
    if (!wanted) return { match: null, candidates: [] }
    const exact = options.find((o) => this.normalize(o.textContent) === wanted)
    if (exact) return { match: exact, candidates: [exact] }
    const candidates = options.filter((o) => this.normalize(o.textContent).startsWith(wanted))
    return { match: candidates.length === 1 ? candidates[0] : null, candidates }
  }

  normalize(text) {
    return (text || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()
  }

  // ---- panel rendering (DOM APIs only — research output is untrusted) ----

  renderPanel(data, { filled }) {
    if (!this.hasPanelTarget) return
    const panel = this.panelTarget
    panel.textContent = ""

    const head = document.createElement("div")
    head.className = "ai-research__panel-head"
    const title = document.createElement("strong")
    title.textContent = data.found === false ? "Airframe not identified" : "Airframe research"
    head.appendChild(title)
    if (data.confidence) {
      const chip = document.createElement("span")
      chip.className = `ai-chip ai-chip--${["high", "medium", "low"].includes(data.confidence) ? data.confidence : "low"}`
      chip.textContent = `${data.confidence} confidence`
      head.appendChild(chip)
    }
    panel.appendChild(head)

    const rows = document.createElement("dl")
    rows.className = "ai-research__rows"
    const operatorNote = this.operatorNote(data.operator, filled)
    this.addRow(rows, "Identified as", [data.aircraft_type, data.variant && `(${data.variant})`].filter(Boolean).join(" "))
    this.addRow(rows, "Registration", data.registration)
    this.addRow(rows, "MSN", data.serial_number)
    this.addRow(rows, "Operator", operatorNote)
    this.addRow(rows, "Previous operators", this.list(data.operator_history))
    this.addRow(rows, "Previous registrations", this.list(data.registration_history))
    this.addRow(rows, "First flight / delivery", data.first_flight_or_delivery)
    this.addRow(rows, "Engines", data.engines)
    this.addRow(rows, "Cabin configuration", data.cabin_configuration)
    this.addRow(rows, "Modifications", this.list(data.modifications))
    this.addRow(rows, "Status", data.airframe_status)
    this.addRow(rows, "Notes", data.notes)
    panel.appendChild(rows)

    const sources = Array.isArray(data.sources) ? data.sources.filter((s) => s && /^https?:\/\//.test(s.url || "")) : []
    if (sources.length) {
      const wrap = document.createElement("div")
      wrap.className = "ai-research__sources"
      const label = document.createElement("span")
      label.textContent = "Sources:"
      wrap.appendChild(label)
      sources.forEach((source) => {
        const link = document.createElement("a")
        link.href = source.url
        link.target = "_blank"
        link.rel = "noopener noreferrer"
        link.textContent = source.title || source.url
        wrap.appendChild(link)
      })
      panel.appendChild(wrap)
    }

    const disclaimer = document.createElement("div")
    disclaimer.className = "ai-research__disclaimer"
    disclaimer.textContent = "AI-researched from public sources — verify against the aircraft's controlled records before saving."
    panel.appendChild(disclaimer)

    panel.classList.remove("d-none")
  }

  operatorNote(operator, filled) {
    if (!operator) return ""
    if (filled.includes("operator")) {
      const chosen = this.hasOperatorTarget ? this.operatorTarget.selectedOptions[0]?.textContent?.trim() : null
      return chosen && chosen !== operator ? `${operator} — matched to ${chosen}` : `${operator} — matched to your operator list`
    }
    if (this.hasOperatorTarget && this.operatorTarget.value) return operator
    const candidates = this.hasOperatorTarget ? this.matchOption(this.operatorTarget, operator).candidates : []
    if (candidates.length > 1) {
      return `${operator} — ambiguous: ${candidates.map((o) => o.textContent.trim()).join(" / ")} — choose the right one`
    }
    if (this.canCreateOperatorValue && this.hasOperatorTarget) return this.addOperatorOffer(operator)
    return `${operator} — not in your operator list; ask an administrator to add the organisation first`
  }

  // The researched operator is unknown here, e.g. an airframe that changed
  // hands weeks ago. One click creates it as an operator and selects it.
  addOperatorOffer(name) {
    const wrap = document.createElement("span")
    wrap.appendChild(document.createTextNode(`${name} — not in your operator list. `))
    const button = document.createElement("button")
    button.type = "button"
    button.className = "btn btn-outline-primary btn-sm"
    button.textContent = `Add “${name}” as a new operator`
    button.addEventListener("click", () => this.createOperator(name, wrap, button))
    wrap.appendChild(button)
    return wrap
  }

  async createOperator(name, wrap, button) {
    button.disabled = true
    try {
      const response = await fetch(this.organizationsUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content || ""
        },
        body: JSON.stringify({ organization: { name, organization_type: "operator" } })
      })
      const payload = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(payload.error || "Could not add the operator.")
      const select = this.operatorTarget
      this.filling = true
      select.appendChild(new Option(payload.name, String(payload.id), true, true))
      select.value = String(payload.id)
      select.classList.add("ai-filled")
      select.dispatchEvent(new Event("change", { bubbles: true }))
      this.filling = false
      wrap.textContent = `${name} — added as a new operator and selected`
    } catch (error) {
      button.disabled = false
      wrap.appendChild(document.createTextNode(` ${error.message}`))
    }
  }

  addRow(rows, label, value) {
    if (!value) return
    const dt = document.createElement("dt")
    dt.textContent = label
    const dd = document.createElement("dd")
    if (value instanceof Node) dd.appendChild(value)
    else dd.textContent = value
    rows.appendChild(dt)
    rows.appendChild(dd)
  }

  list(values, separator = " · ") {
    return Array.isArray(values) ? values.filter(Boolean).join(separator) : ""
  }

  // ---- small helpers ----

  value(targets) {
    return targets[0] ? targets[0].value.trim() : ""
  }

  setStatus(message, tone) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = ""
    if (tone === "busy") {
      const spinner = document.createElement("span")
      spinner.className = "spinner-border spinner-border-sm me-1"
      spinner.setAttribute("aria-hidden", "true")
      this.statusTarget.appendChild(spinner)
    }
    this.statusTarget.appendChild(document.createTextNode(message))
    this.statusTarget.dataset.tone = tone || "idle"
  }
}
